import type { Dayjs } from 'dayjs';
import type { QueryClient } from 'react-query';
import NewAnalyticsApi from 'api/new-analytics';
import type { PerformanceResponse } from 'api/new-analytics/types';
import { queryKey } from 'constants/queryKey';

interface ResolvePerformanceCsvCompareParamsInput {
  section: 'performance' | 'content';
  timezone: string;
  isComparisonEnabled: boolean;
  comparisonStartDate: Dayjs;
  comparisonEndDate: Dayjs;
  startDate: Dayjs;
  endDate: Dayjs;
  sortedPageIds: number[];
}

function hasComparablePeriod(data: PerformanceResponse | undefined): boolean {
  const since = data?.period?.compare_since;
  const until = data?.period?.compare_until;
  return since != null && since !== '' && until != null && until !== '';
}

export async function resolvePerformanceCsvCompareParams(
  queryClient: QueryClient,
  input: ResolvePerformanceCsvCompareParamsInput,
): Promise<{ compareSince?: string; compareUntil?: string }> {
  const {
    section,
    timezone,
    isComparisonEnabled,
    comparisonStartDate,
    comparisonEndDate,
    startDate,
    endDate,
    sortedPageIds,
  } = input;

  if (section !== 'performance' || sortedPageIds.length === 0) {
    return {};
  }

  const since = startDate.format('YYYY-MM-DD');
  const until = endDate.format('YYYY-MM-DD');

  if (isComparisonEnabled) {
    return {
      compareSince: comparisonStartDate.format('YYYY-MM-DD'),
      compareUntil: comparisonEndDate.format('YYYY-MM-DD'),
    };
  }

  const key = queryKey.analyticsPerformance(
    sortedPageIds,
    since,
    until,
    undefined,
    undefined,
  );

  const cached = queryClient.getQueryData<PerformanceResponse>(key);
  let data = cached != null && hasComparablePeriod(cached) ? cached : undefined;

  if (!data) {
    data = await queryClient.fetchQuery(key, () =>
      NewAnalyticsApi.getPerformance({
        page_ids: sortedPageIds,
        since,
        until,
        timezone,
        include: 'time_series',
      }),
    );
  }

  return {
    compareSince: data?.period?.compare_since,
    compareUntil: data?.period?.compare_until,
  };
}
